import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CallRoute, Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { StepOptions } from '../../helpers/add-step-helpers';
import { useCallObjectsData } from '../../use-call-objects-data';
import { CallGroupStep } from './call-group-step';
import { CallQueueStep } from './call-queue-step';
import { CallRouteStep } from './call-route-step';
import { DeviceStep } from './device-step';
import { ForwardingNumberStep } from './forwarding-number-step';
import { PhoneHoursStep } from './phone-hours-step';
import { PhoneTreeDuplicateStep } from './phone-tree-duplicate-step/phone-tree-duplicate-step';
import { PhoneTreeIntermediateStep } from './phone-tree-intermediate-step';
import { PhoneTreeStep } from './phone-tree-step';
import { PlayMessageStep } from './play-message-step';
import { StepContext } from './provider';
import { PhoneTreeAncestors, RepeatStep } from './repeat-step';
import { SelectStep } from './select-step';
import { AdditionalNodeData, isNodeStateOfType, NodeState, NodeStateWithAdditionalData } from './types';
import { getRepeatPhoneTreeName } from './utils';
import { VoicemailStep } from './voicemail-step';
import { WeaveAssistantStep } from './weave-assistant-step';

export type WizardStep = 'SELECT_TYPE' | 'CONFIG' | 'PHONE_TREE_INTERMEDIATE' | 'PHONE_TREE_DUPLICATE';

export const AddStepPanel = ({
  stepOptions,
  omitBackActions = false,
  initialState,
  callObjectsData,
  callRoute,
  isDeviceExtensionRoute = false,
  onClose,
  onAddStep,
  onDuplicateSubGraph,
  getAncestorPhoneTrees,
  processingNodeId,
  isCalendarViewOpen,
  onCalendarViewChange,
}: {
  stepOptions: StepOptions;
  omitBackActions?: boolean;
  initialState?: NodeStateWithAdditionalData;
  callObjectsData: ReturnType<typeof useCallObjectsData>;
  isDeviceExtensionRoute?: boolean;
  callRoute: CallRoute;
  onClose: () => void;
  onAddStep: (props: NodeStateWithAdditionalData) => void;
  onDuplicateSubGraph: (args: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] }) => void;
  getAncestorPhoneTrees: () => PhoneTreeAncestors;
  processingNodeId?: string;
  isCalendarViewOpen?: boolean;
  onCalendarViewChange?: (isOpen: boolean) => void;
}) => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const [state, setState] = useState<NodeStateWithAdditionalData | undefined>(initialState);

  const getInitialHistory = useCallback((initialStateData?: NodeStateWithAdditionalData): WizardStep[] => {
    if (!initialStateData?.nodeState.callObject?.primitiveName) {
      // If there is no primitiveName then we consider that there is no initial state and we start from the
      // beginning (selecting a step type).
      return ['SELECT_TYPE'];
    }

    return ['SELECT_TYPE', 'CONFIG'];
  }, []);

  const [stepHistory, setStepHistory] = useState<WizardStep[]>(() => getInitialHistory(initialState));
  const currentStep = stepHistory[stepHistory.length - 1];
  const alerts = useAlert();
  const [isEditMode] = useState(() => !!initialState?.nodeState.callObject?.primitiveName);

  const handleSelectStepProceedClick = (type: CallRouteV1.Types.AllNodeTypesCovered['type']) => {
    const typeIsDifferent = state?.nodeState.type !== type;

    const defaultCallObject = {
      primitiveId: '',
      primitiveName: '',
      instructionId: '',
      instructionSetId: '',
    };

    // Repeat is a special case where if there is only one phone tree ancestor node, we don't need to show the next
    // step and we can just add the step
    if (type === CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT) {
      const ancestorPhoneTrees = getAncestorPhoneTrees();

      if (Object.keys(ancestorPhoneTrees).length === 1) {
        const phoneTreeNode = Object.values(ancestorPhoneTrees)[0].node;

        onAddStep({
          nodeState: {
            type,
            callObject: {
              ...defaultCallObject,
              primitiveName: getRepeatPhoneTreeName(phoneTreeNode),
              primitiveId: '',
              repeatExpansion: {
                phoneTreeNodeId: phoneTreeNode.id,
              },
            },
          },
          additionalData: {},
        });
        return;
      }
    }

    if (typeIsDifferent) {
      setState({
        nodeState: {
          type,
          callObject: defaultCallObject,
        },
        additionalData: {},
      });
    }

    if (
      type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE &&
      !(isEditMode && initialState?.nodeState.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE)
    ) {
      // If the selected type is Phone Tree then we need to go to the intermediate step but only if we are not editing
      // an existing Phone Tree node (since for those we go directly to the config step).
      setStepHistory((prev) => [...prev, 'PHONE_TREE_INTERMEDIATE']);
    } else {
      setStepHistory((prev) => [...prev, 'CONFIG']);
    }
  };

  const handleAddStep = useCallback(
    (updates: NodeState, additionalData?: AdditionalNodeData) => {
      const totalUpdates = {
        nodeState: {
          ...state?.nodeState,
          ...updates,
        },
        additionalData: {
          ...state?.additionalData,
          ...additionalData,
        },
      };

      onAddStep(totalUpdates);
    },
    [state, onAddStep]
  );

  const handlePushStep = useCallback((nextStep: WizardStep) => {
    setStepHistory((prev) => [...prev, nextStep]);
  }, []);

  const handleBack = useCallback(() => {
    setStepHistory((prev) => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  const handleCancelClick = useCallback(() => {
    if (!isEditMode) {
      // If the user clicks on cancel when adding a new step (not editing an existing node)
      // then we just close the panel.
      onClose();
      return;
    }

    // If the user clicks on cancel when editing an existing node, if the state has not changed
    // then this means that the user is on the initial step that was brought up when the panel
    // was opened. In this case we just close the panel.
    if (state === initialState) {
      onClose();
      return;
    }

    // If the user clicks on cancel when editing an existing node and the state has changed
    // then this means that the user has changed the step type and we need to reset the state
    // to the initial state.
    setStepHistory(getInitialHistory(initialState));
    setState(initialState);
  }, [isEditMode, onClose, state, initialState, getInitialHistory]);

  // Check if the step is changing based on the initial state and current state types.
  // This is used to determine if the step is changing when the user is editing an existing node.
  // If the initial state type is different from the current state type, then we consider it as changing step.
  // This is used to show the correct UI and handle the back button correctly.
  const isChangingStep = useMemo(() => {
    return !!initialState?.nodeState.type && initialState.nodeState.type !== state?.nodeState.type;
  }, [initialState, state]);

  const omitBackButton = useMemo(() => {
    return omitBackActions || stepHistory.length === 1;
  }, [omitBackActions, stepHistory.length]);

  const commonProps = useMemo(
    () => ({
      initialState: state?.nodeState,
      callObjectsData,
      tenantId,
      settingsTenantLocation,
      isDeviceExtensionRoute,
      callRoute,
      onClose,
      onBackClick: handleBack,
      onCancelClick: handleCancelClick,
      onProceedClick: handleAddStep,
    }),
    [
      state?.nodeState,
      callObjectsData,
      tenantId,
      settingsTenantLocation,
      isDeviceExtensionRoute,
      callRoute,
      onClose,
      handleBack,
      handleCancelClick,
      handleAddStep,
    ]
  );

  const getNodeConfigComponent = () => {
    if (!state?.nodeState.type) {
      alerts.error('Unknown step type');
      return null;
    }

    if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP)) {
      return <CallGroupStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE)) {
      return <CallQueueStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE)) {
      return <DeviceStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER)) {
      return <ForwardingNumberStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX)) {
      return <VoicemailStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE)) {
      return <PlayMessageStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE)) {
      return (
        <PhoneTreeStep
          {...commonProps}
          initialState={state.nodeState}
          initialDialOptions={state.additionalData.dialOptions}
        />
      );
    } else if (
      // Device extension nodes and call route nodes use the same component since they are treated the same way
      isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE) ||
      isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION)
    ) {
      return <CallRouteStep {...commonProps} initialState={state.nodeState} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS)) {
      return (
        <PhoneHoursStep
          {...commonProps}
          initialState={state.nodeState}
          isCalendarViewOpen={isCalendarViewOpen}
          onCalendarViewChange={onCalendarViewChange}
        />
      );
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT)) {
      const ancestorPhoneTrees = getAncestorPhoneTrees();
      return <RepeatStep {...commonProps} initialState={state.nodeState} phoneTreeNodes={ancestorPhoneTrees} />;
    } else if (isNodeStateOfType(state.nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT)) {
      return <WeaveAssistantStep {...commonProps} initialState={state.nodeState} />;
    } else {
      return (
        <SelectStep
          stepOptions={stepOptions}
          initialStep={state?.nodeState.type}
          onProceedClick={handleSelectStepProceedClick}
        />
      );
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'SELECT_TYPE':
        return (
          <SelectStep
            stepOptions={stepOptions}
            initialStep={state?.nodeState.type}
            onProceedClick={handleSelectStepProceedClick}
          />
        );
      case 'PHONE_TREE_INTERMEDIATE':
        return <PhoneTreeIntermediateStep onNext={handlePushStep} />;
      case 'PHONE_TREE_DUPLICATE':
        return (
          <PhoneTreeDuplicateStep
            onDoneClick={onDuplicateSubGraph}
            currentCallRouteId={callRoute.callRouteId}
            callRouteLocationId={callRoute.locationId}
            callRouteName={callRoute.name}
            processingNodeId={processingNodeId}
          />
        );
      case 'CONFIG':
        return getNodeConfigComponent();
      default:
        return null;
    }
  };

  const contextValue = useMemo(
    () => ({
      isEditMode,
      isChangingStep,
      omitBackActions: omitBackButton,
      onCancelClick: handleCancelClick,
      onBackClick: handleBack,
      onClose,
    }),
    [handleCancelClick, handleBack, onClose, isEditMode, isChangingStep, omitBackButton]
  );

  return (
    <StepContext.Provider value={contextValue}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(2)};
          height: 100%;
        `}
        onKeyDown={(e) => {
          // Stop propagation of keydown events to prevent the call route canvas from capturing
          // them and affecting the canvas zoom state.
          e.stopPropagation();
        }}
      >
        {renderCurrentStep()}
      </div>
    </StepContext.Provider>
  );
};
